import React, { useEffect, useState, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import { FaBell, FaPlay, FaExternalLinkAlt } from "react-icons/fa";
import Modal from "shared/components/UIElements/Modal";
import { useTranslation } from "react-i18next";

import "./NotificationBell.css";

const NotificationBell = ({ items, version = "1" }) => {
  const { t } = useTranslation();
  const [hasUnread, setHasUnread] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [videoModal, setVideoModal] = useState({ show: false, url: null, title: "" });

  const openVideoModal = useCallback((url, title) => {
    setVideoModal({ show: true, url, title });
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoModal({ show: false, url: null, title: "" });
  }, []);

  // Initialize unread badge state based on version vs localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("notificationBellVersion");
      if (String(stored) === String(version)) {
        setHasUnread(false);
      } else {
        setHasUnread(true);
      }
    } catch (e) {
      setHasUnread(true);
    }
  }, [version]);

  // Auto-open modal for new version, without clearing the badge
  useEffect(() => {
    if (!hasUnread) return;
    try {
      const shown = localStorage.getItem("notificationBellModalShown");
      if (String(shown) !== String(version)) {
        setShowModal(true);
        localStorage.setItem("notificationBellModalShown", String(version));
      }
    } catch (e) {}
  }, [version, hasUnread]);

  return (
    <>
      {/* Video Modal - Lazy loaded */}
      {videoModal.show && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <div className="video-modal-title">{videoModal.title || "Video"}</div>
              <button className="video-modal-close" onClick={closeVideoModal}>
                ×
              </button>
            </div>
            <video className="video-modal-player" src={videoModal.url} controls autoPlay playsInline />
          </div>
        </div>
      )}

      <Modal
        show={showModal}
        onCancel={() => setShowModal(false)}
        style={{ minHeight: "200px" }}
        header={t("notifications")}
        contentClass="notification-modal-content"
      >
        <div style={{ padding: "4px 16px 16px 16px", maxWidth: 760 }}>
          {(items || []).length === 0 && (
            <div style={{ padding: "10px 12px", fontSize: 14, color: "#555" }}>Bildirim yok</div>
          )}
          {(items || []).map((n, idx) => {
            const isLast = idx === (items || []).length - 1;
            return (
              <div
                key={n.id}
                style={{
                  padding: "8px 0 14px 0",
                  borderBottom: isLast ? "none" : "1px solid #e0e0e0",
                  marginTop: idx > 0 ? 10 : 0,
                }}
              >
                <div style={{ fontSize: 18, fontWeight: 600, margin: "6px 0 6px 0" }}>
                  <span style={{ marginRight: 6 }}>•</span>
                  {n.title}
                </div>
                {n.description && (
                  <div
                    style={{
                      fontSize: 15,
                      color: "#111",
                      backgroundColor: "#f6f6f6",
                      border: "1px solid #e2e2e2",
                      borderRadius: 6,
                      padding: "10px 12px",
                      margin: "6px 0 10px 0",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {n.description}
                  </div>
                )}
                <div className="notification-actions">
                  {n.videoUrl && (
                    <button
                      className="notification-video-btn"
                      onClick={() => openVideoModal(n.videoUrl, n.videoTitle || n.title)}
                    >
                      <FaPlay size={12} />
                      <span>{n.videoButtonText || "Videoyu İzle"}</span>
                    </button>
                  )}
                  {n.linkUrl && (
                    <a className="notification-link-btn" href={n.linkUrl} target="_blank" rel="noopener noreferrer">
                      <FaExternalLinkAlt size={11} />
                      <span>{n.linkText || "Bağlantıyı Aç"}</span>
                    </a>
                  )}
                </div>
                {n.time && <div style={{ fontSize: 13, color: "#666" }}>{n.time}</div>}
              </div>
            );
          })}
        </div>
      </Modal>

      <Dropdown
        className="notifications-dropdown"
        align="end"
        onToggle={(isOpen) => {
          if (isOpen && hasUnread) {
            try {
              localStorage.setItem("notificationBellVersion", String(version));
            } catch (e) {}
            setHasUnread(false);
          }
        }}
      >
        <Dropdown.Toggle variant="success" id="dropdown-notifications" className="notification-bell-btn">
          <span style={{ fontSize: "16px", paddingLeft: "8px" }}>{t("notifications")}</span>
          <FaBell size={18} />
          {hasUnread && <span className="notification-badge">!</span>}
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="notification-dropdown-menu"
          style={{ maxWidth: "200px", textAlign: "left", gap: "0px" }}
        >
          {(items || []).length === 0 && (
            <div style={{ padding: "10px 12px", fontSize: 14, color: "#ccc" }}>No notifications</div>
          )}
          {(items || []).map((n, idx) => {
            const isLast = idx === (items || []).length - 1;
            return (
              <div
                key={n.id}
                style={{
                  padding: "16px 16px",
                  borderBottom: isLast ? "none" : "1px solid #444",
                  marginTop: idx === 0 ? 6 : 0,
                  marginBottom: isLast ? 4 : 0,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>{n.title}</div>
                {n.description && (
                  <div style={{ fontSize: 13, color: "#ddd", marginTop: 8, whiteSpace: "pre-line" }}>
                    {n.description}
                  </div>
                )}
                <div className="notification-actions">
                  {n.videoUrl && (
                    <button
                      className="notification-video-btn notification-video-btn-dark"
                      onClick={(e) => {
                        e.stopPropagation();
                        openVideoModal(n.videoUrl, n.videoTitle || n.title);
                      }}
                    >
                      <FaPlay size={10} />
                      <span>{n.videoButtonText || "Videoyu İzle"}</span>
                    </button>
                  )}
                  {n.linkUrl && (
                    <a
                      className="notification-link-btn notification-link-btn-dark"
                      href={n.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaExternalLinkAlt size={9} />
                      <span>{n.linkText || "Bağlantıyı Aç"}</span>
                    </a>
                  )}
                </div>
                {n.time && <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{n.time}</div>}
              </div>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

export default NotificationBell;
